/**
 * A researcher's Reflections page, laid out like a lab page: a cover (image or colour) with their name and
 * headline; on the left the cover contents (their own text, and from the profile the summary, the CV at a
 * glance and the résumé) and their reflections on a timeline; on the right the tags to filter by and who
 * the author is. Owners edit the page and write from here; a public version (Otable logo, no app around it)
 * lives at /p/reflections/:userId/.
 * Filters come from the address too: ?item=<model>:<id> (the reflections on one profile item, from the
 * reflect buttons on the profile's Experience, Education, Publications and Projects), ?tag=, ?month=YYYY-MM.
 * Backend: GET /reflections/page/?author=, /reflections/timeline/?author=, /public/reflections/<id>/.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Avatar, Box, Button, Chip, CircularProgress, Container, Stack, Typography } from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, LocalOffer as TagIcon, Public as PublicIcon, School as SchoolIcon, Work as WorkIcon,
} from '@mui/icons-material';
import { fetcher } from 'shared/helpers/axios';
import LabMarkdown from 'components/collab/lab/LabMarkdown';
import { Entry } from '../ReflectionsTab';
import ReflectionEditor from './ReflectionEditor';
import ReflectionsPageDialog from './ReflectionsPageDialog';
import { CoverHero, PageBackground, PublicFooter, Section, pageColor } from './ReflectionsShell';

const heroButton = { color: '#fff', borderColor: 'rgba(255,255,255,0.8)', bgcolor: 'rgba(0,0,0,0.18)', fontWeight: 600, textShadow: 'none',
  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(0,0,0,0.3)' } };

export default function ReflectionsPage({ publicMode = false }) {
  const { userId } = useParams();
  const navigate = useNavigate();
  const pageKey = publicMode ? `/public/reflections/${userId}/` : `/reflections/page/?author=${userId}`;
  const { data: page, error, mutate } = useSWR(userId ? pageKey : null, fetcher);
  const { data: own, mutate: mutateTimeline } = useSWR(!publicMode && userId ? `/reflections/timeline/?author=${userId}` : null, fetcher);
  const timeline = publicMode ? page?.timeline : own;
  const [params] = useSearchParams();
  const [tag, setTag] = useState(params.get('tag') || null);
  const [month, setMonth] = useState(params.get('month') || null);       // 'YYYY-MM' from the archive card
  const [item, setItem] = useState(params.get('item') || null);          // '<model>:<id>' of a profile item
  const listRef = useRef(null);
  // arriving with a filter (e.g. from a profile item): go straight to the reflections
  useEffect(() => {
    if ((params.get('item') || params.get('tag') || params.get('month')) && listRef.current) {
      listRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  });   // eslint-disable-line react-hooks/exhaustive-deps
  const [editPage, setEditPage] = useState(false);
  const [writing, setWriting] = useState(false);

  const monthKey = (iso) => iso.slice(0, 7);
  const [itemModel, itemId] = (item || '').split(':');
  const onItem = (e) => e.item && e.item.content_type.split('.').pop() === itemModel && String(e.item.object_id) === itemId;
  const entries = useMemo(() => (timeline?.entries || [])
    .filter((e) => (!tag || e.tags.includes(tag)) && (!month || monthKey(e.date) === month) && (!item || onItem(e))),
  [timeline, tag, month, item]);   // eslint-disable-line react-hooks/exhaustive-deps
  const itemInfo = item && ((timeline?.entries || []).find(onItem)?.item
    || { kind: { workexperience: 'Experience', education: 'Education', publication: 'Publication', project: 'Project' }[itemModel] || 'Item', label: '' });
  // the archive: years, newest first, each with its months and how many reflections each holds
  const archive = useMemo(() => {
    const years = new Map();
    (timeline?.entries || []).forEach((e) => {
      const y = e.date.slice(0, 4);
      const m = monthKey(e.date);
      if (!years.has(y)) years.set(y, new Map());
      years.get(y).set(m, (years.get(y).get(m) || 0) + 1);
    });
    return [...years.entries()].map(([y, ms]) => ({ year: y, months: [...ms.entries()] }));
  }, [timeline]);
  const monthName = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString(undefined, { month: 'long' });
  const linkTo = (e) => (publicMode ? `/p/reflection/${e.id}/` : `/reflections/${e.id}/`);
  const toggle = (t) => setTag(tag === t ? null : t);

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Alert severity="info">{publicMode ? 'This Reflections page is not public.' : 'This Reflections page could not be loaded.'}</Alert>
      </Container>
    );
  }
  if (!page) return <Box display="grid" sx={{ placeItems: 'center', py: 10 }}><CircularProgress /></Box>;

  const color = pageColor(page);
  const owner = page.is_owner;
  const hasCover = page.about || page.summary || page.resume || page.cv?.length;

  return (
    <PageBackground page={page} publicMode={publicMode}>
      <CoverHero cover={page} publicMode={publicMode} minHeight={280}>
        <Box display="flex" alignItems="flex-end" gap={2.5} flexWrap="wrap">
          <Avatar src={page.user.avatar_url || undefined}
            sx={{ width: 96, height: 96, border: '3px solid #fff', fontSize: 36, bgcolor: 'rgba(255,255,255,0.25)', boxShadow: '0 2px 8px rgba(0,0,0,0.25)' }}>
            {(page.user.name || '?')[0]}
          </Avatar>
          <Box flex={1} minWidth={240}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', opacity: 0.85 }}>Reflections</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.6rem' }, lineHeight: 1.15 }}>{page.user.name}</Typography>
            {page.headline && <Typography sx={{ fontSize: { xs: 16, md: 19 }, mt: 0.75, opacity: 0.95 }}>{page.headline}</Typography>}
          </Box>
          {owner && (
            <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setWriting(true)}
                sx={{ bgcolor: '#fff', color, fontWeight: 700, textShadow: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.88)' } }}>
                Write a reflection
              </Button>
              <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditPage(true)} sx={heroButton}>Edit page</Button>
              {page.is_public && (
                <Button variant="outlined" startIcon={<PublicIcon />} component="a" href={`/p/reflections/${page.user.id}/`}
                  target="_blank" rel="noopener noreferrer" sx={heroButton}>Public page</Button>
              )}
            </Stack>
          )}
        </Box>
      </CoverHero>

      <Container maxWidth="lg" sx={{ mt: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, alignItems: 'start' }}>
          <Box minWidth={0}>
            {/* the cover contents: the owner's own text, and from the profile what they switched on */}
            {page.about && <Section title="About" color={color}><LabMarkdown>{page.about}</LabMarkdown></Section>}
            {page.summary && <Section title="Summary" color={color}><LabMarkdown>{page.summary}</LabMarkdown></Section>}
            {page.resume && <Section title="Résumé" color={color}><LabMarkdown>{page.resume}</LabMarkdown></Section>}
            {page.cv?.length > 0 && (
              <Section title="CV at a glance" color={color}>
                <Stack spacing={1.25}>
                  {page.cv.map((c, i) => (
                    <Box key={i} display="flex" gap={1.25} alignItems="flex-start">
                      {c.kind === 'position' ? <WorkIcon sx={{ color, fontSize: 20, mt: 0.25 }} /> : <SchoolIcon sx={{ color, fontSize: 20, mt: 0.25 }} />}
                      <Box flex={1}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{c.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{[c.place, c.years].filter(Boolean).join(' · ')}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Section>
            )}
            {owner && !hasCover && (
              <Section color={color}>
                <Typography variant="body2" color="text.secondary">
                  Your cover is empty. Use “Edit page” to write about these reflections and to show your summary, CV and résumé from your profile.
                </Typography>
              </Section>
            )}

            {/* the reflections */}
            <Box ref={listRef} sx={{ scrollMarginTop: 80 }} />
            <Section title="Reflections" color={color}
              action={(tag || month || item) && (
                <Box display="flex" gap={0.75} flexWrap="wrap" justifyContent="flex-end">
                  {item && <Chip size="small" label={`${itemInfo.kind}${itemInfo.label ? `: ${itemInfo.label}` : ''}`.slice(0, 60)}
                    onDelete={() => setItem(null)} color="primary" />}
                  {month && <Chip size="small" label={`${monthName(month)} ${month.slice(0, 4)}`} onDelete={() => setMonth(null)} color="primary" />}
                  {tag && <Chip size="small" label={tag} onDelete={() => setTag(null)} color="primary" />}
                </Box>
              )}>
              {!timeline ? <Box display="flex" justifyContent="center" py={3}><CircularProgress size={28} /></Box>
                : entries.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {owner ? 'Nothing here yet. “Write a reflection” starts your first one.'
                      : timeline.locked ? `${page.user.name.split(' ')[0]}'s reflections are for their connections. Ask to connect from their profile.`
                        : 'No reflections to show here yet.'}
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {entries.map((e) => <Entry key={e.id} e={e} activeTag={tag} onTag={toggle} linkTo={linkTo} />)}
                  </Stack>
                )}
              {owner && item && !publicMode && (
                <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1.5 }} onClick={() => setWriting('item')}>
                  Write a reflection on this {itemInfo.kind.toLowerCase()}
                </Button>
              )}
              {owner && page.is_public && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  On your public page: {(timeline?.entries || []).filter((e) => e.visibility === 'public' && e.status === 'published').length} of these.
                  To add one, open it, choose Edit and set “Who can read it” to “Anyone, on my public page”.
                </Typography>
              )}
              {timeline?.hidden > 0 && !publicMode && (entries.length > 0 || !timeline.locked) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                  {timeline.hidden} more {timeline.hidden === 1 ? 'is' : 'are'} shared with {page.user.name.split(' ')[0]}&apos;s connections only.
                </Typography>
              )}
            </Section>
          </Box>

          {/* right: tags and the author */}
          <Box sx={{ position: { md: 'sticky' }, top: { md: 80 } }}>
            <Section title="Tags" color={color}>
              {timeline?.tags?.length ? (
                <Box display="flex" gap={0.75} flexWrap="wrap">
                  {timeline.tags.slice(0, 30).map(({ tag: t, count }) => (
                    <Chip key={t} size="small" icon={<TagIcon sx={{ fontSize: 14 }} />} label={`${t} (${count})`} onClick={() => toggle(t)}
                      color={tag === t ? 'primary' : 'default'} variant={tag === t ? 'filled' : 'outlined'} />
                  ))}
                </Box>
              ) : <Typography variant="body2" color="text.secondary">Tags appear with the first reflections.</Typography>}
            </Section>
            {/* every reflection by title; the same cards on the public page, also while it has none yet */}
            <Section title="All reflections" color={color}>
              {!timeline?.entries?.length ? (
                <Typography variant="body2" color="text.secondary">{publicMode ? 'No public reflections yet.' : 'No reflections yet.'}</Typography>
              ) : (
                <Stack spacing={1} sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
                  {timeline.entries.map((e) => (
                    <Box key={e.id} onClick={() => navigate(linkTo(e))}
                      sx={{ cursor: 'pointer', '&:hover .t': { color, textDecoration: 'underline' } }}>
                      <Typography className="t" variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>{e.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(e.date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                        {e.status === 'draft' ? ' · draft' : ''}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              )}
            </Section>
            {/* the reflections by date: a month filters the list */}
            <Section title="By date" color={color}>
              {!archive.length ? (
                <Typography variant="body2" color="text.secondary">Months appear with the first reflections.</Typography>
              ) : (
                <Stack spacing={1.25}>
                  {archive.map(({ year, months }) => (
                    <Box key={year}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{year}</Typography>
                      {months.map(([m, n]) => (
                        <Box key={m} onClick={() => setMonth(month === m ? null : m)} role="button"
                          sx={{ display: 'flex', justifyContent: 'space-between', pl: 1.5, py: 0.25, borderRadius: 1, cursor: 'pointer',
                            bgcolor: month === m ? 'action.selected' : 'transparent', '&:hover': { bgcolor: 'action.hover' } }}>
                          <Typography variant="body2" sx={{ color: month === m ? color : 'text.primary', fontWeight: month === m ? 600 : 400 }}>{monthName(m)}</Typography>
                          <Typography variant="body2" color="text.secondary">{n}</Typography>
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Stack>
              )}
            </Section>
          </Box>
        </Box>
        {publicMode && <PublicFooter />}
      </Container>

      {owner && (
        <>
          <ReflectionsPageDialog open={editPage} page={page} onClose={() => setEditPage(false)}
            onSaved={() => { setEditPage(false); mutate(); }} />
          <ReflectionEditor open={Boolean(writing)}
            entry={writing === 'item' ? { item: itemInfo, newItem: { content_type_name: `registry_user.${itemModel}`, object_id: itemId } } : null}
            tagSuggestions={(timeline?.tags || []).map((t) => t.tag)}
            onClose={() => setWriting(false)} onSaved={(id) => { setWriting(false); mutateTimeline(); navigate(`/reflections/${id}/`); }} />
        </>
      )}
    </PageBackground>
  );
}

/** The older address of one item's reflections: the Reflections page, filtered to that item. */
export function ItemReflectionsRedirect() {
  const { userId, contentType, objectId } = useParams();
  return <Navigate replace to={`/profile/${userId}/reflections/?item=${contentType.split('.').pop()}:${objectId}`} />;
}
